import React, { useEffect, useRef, useState } from 'react';
import * as PropTypes from 'prop-types';
import { Button } from '@design-system-etat/dsfr-react';
import { Controller, useFormContext } from 'react-hook-form';
import FileUploadeFront from './Model/FileUploadeFront';
import FileUploadeBack from '../../../form/InputFiles/Model/FileUploadeBack';

const InputFiles = ({
    label,
    texteAide,
    name,
    error,
    warning,
    valid,
    required,
    disabled,
    multiple,
    accept,
    tailleMoMaxUnFichier,
    tailleMoMaxTotalFichiers,
    nombreMaxFichiers,
    licenceFichier,
}) => {
    const { setValue, watch, trigger } = useFormContext();
    const [isLoading, setIsLoading] = useState({});
    const refInput = useRef(null);

    const validation = {
        validate: {
            tailleFichiers: (fichiers) => {
                if (!fichiers) return true;

                let validate = true;
                let totalTaille = 0;
                let type = '';
                let reformatedType = '';
                fichiers.forEach((fichier) => {
                    if (fichier instanceof FileUploadeBack) {
                        return true;
                    }

                    if (fichier.file) {
                        type = fichier.file.type;
                        reformatedType = `.`.concat(type.split('/')[1]);
                    } else {
                        reformatedType = fichier.filename.split('.').pop();
                    }
                    if (!accept.includes(reformatedType)) {
                        validate = `Les fichiers de ce type ne sont pas acceptés. `;
                    }
                    if (fichier.file) {
                        totalTaille += fichier.file.size;
                        if (fichier.file.size > tailleMoMaxUnFichier * 1024 * 1024) {
                            validate = `La taille de chaque pièce jointe ne doit pas dépasser ${tailleMoMaxUnFichier}Mo.`;
                            return false;
                        }
                    }
                    return true;
                });
                if (totalTaille > tailleMoMaxTotalFichiers * 1024 * 1024) {
                    validate = `La taille totale de vos pièces jointes ne peuvent pas dépasser ${tailleMoMaxTotalFichiers}Mo.`;
                }
                if (fichiers.length > nombreMaxFichiers) {
                    validate = `Le nombre de fichier à importer ne doit pas dépasser un total de ${nombreMaxFichiers}.`;
                }
                return validate;
            },
        },
    };

    if (required) {
        validation.required = `Le champ "${label}" est obligatoire.`;
    }

    validation.disabled = disabled;

    const onChangeFile = (e) => {
        setValue(
            name,
            [...e.target.files].map((file) => new FileUploadeFront(file)),
        );
        if (refInput.current) {
            refInput.current.value = '';
        }
        trigger(name);
    };

    const onClickUpload = () => {
        refInput.current.click();
    };

    const download = async (fileToDownload) => {
        setIsLoading((isLoadingScope) => ({ ...isLoadingScope, [fileToDownload.uuid]: true }));
        await fileToDownload.download();
        setIsLoading((isLoadingScope) => ({ ...isLoadingScope, [fileToDownload.uuid]: false }));
    };

    const removeFile = (fileToRemove) => {
        const fichiers = (watch(name)?.length ? watch(name) : [licenceFichier]) || [];

        if (fichiers !== undefined) {
            setValue(
                name,
                [...fichiers].filter((fichier) => !Object.is(fileToRemove, fichier)),
            );
        }

        trigger(name);
    };

    /**
     * Génération des className en fonction du statut
     */

    let texteValidation = null;
    let inputGroupClassName = 'fr-input-group dsmi-input-group';
    let boutonImporter = 'margin-right-sm';

    if (disabled) {
        inputGroupClassName += ' fr-input-group--disabled';
    } else if (error) {
        inputGroupClassName += ' fr-input-group--error';
        if (error.message) {
            texteValidation = {
                id: `${name}-desc-error`,
                message: error.message,
                className: 'fr-error-text',
            };
        }
    } else if (warning) {
        inputGroupClassName += ' fr-input-group--warning';
        if (warning.message) {
            texteValidation = {
                id: `${name}-desc-warning`,
                message: warning.message,
                className: 'fr-warning-text',
            };
        }
    } else if (valid) {
        inputGroupClassName += ' fr-input-group--valid';
        if (valid.message) {
            texteValidation = {
                id: `${name}-desc-valid`,
                message: valid.message,
                className: 'fr-valid-text',
            };
        }
    }

    if (required) {
        inputGroupClassName += ' required';
    }

    if (!texteAide) {
        boutonImporter = 'margin-top-xs margin-right-sm';
    }
    useEffect(() => {
        if (licenceFichier && Array.isArray(licenceFichier)) {
            setValue(name, licenceFichier);
        }
    }, []);

    return (
        <Controller
            name={name}
            rules={validation}
            render={({ field }) => (
                <div className={inputGroupClassName}>
                    <label className="fr-label" htmlFor={name} id={`${name}-label`}>
                        {label && <span className="fr-label-texte">{label}</span>}
                        {texteAide && (
                            <span id={`${name}-hint`} className="fr-hint-text">
                                {texteAide}
                            </span>
                        )}
                        <input
                            ref={refInput}
                            name={field.name}
                            onChange={(event) => onChangeFile(event)}
                            onBlur={field.onBlur}
                            type="file"
                            className="display-none"
                            multiple={multiple}
                            accept={accept}
                            id={name}
                        />
                    </label>
                    <Button
                        icon="fr-fi-upload-2-line"
                        iconPosition="left"
                        variant="secondary"
                        onClick={onClickUpload}
                        disabled={disabled}
                        className={boutonImporter}
                        type="button"
                    >
                        Importer
                    </Button>
                    {(field.value?.length ? field.value : licenceFichier || []).map((file) => (
                        <div
                            className="fr-tag margin-right-sm margin-top-xs"
                            style={{ wordBreak: 'break-all' }}
                            key={file.uuid ? file.uuid : name}
                        >
                            <span>{file.filename}</span>
                            <Button
                                title="télécharger"
                                size="sm"
                                variant="secondary"
                                icon="fr-fi-file-download-line"
                                onClick={() => download(file)}
                                className="margin-left-xxs"
                                isLoading={isLoading[file.uuid]}
                                type="button"
                            />
                            <Button
                                title="supprimer"
                                size="sm"
                                variant="secondary"
                                icon="fr-fi-delete-line"
                                onClick={() => removeFile(file)}
                                className="margin-left-xxs"
                                type="button"
                            />
                        </div>
                    ))}
                    {texteValidation && (
                        <p id={texteValidation.id} className={texteValidation.className}>
                            {texteValidation.message}
                        </p>
                    )}
                </div>
            )}
        />
    );
};

InputFiles.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    texteAide: PropTypes.string,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    required: PropTypes.bool,
    accept: PropTypes.string,
    tailleMoMaxUnFichier: PropTypes.number,
    tailleMoMaxTotalFichiers: PropTypes.number,
    nombreMaxFichiers: PropTypes.number,
    licenceFichier: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                uuid: PropTypes.string.isRequired,
                filename: PropTypes.string.isRequired,
            }),
        ),
        PropTypes.oneOf([null]),
    ]),
    error: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
    warning: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
    valid: PropTypes.oneOfType([
        PropTypes.shape({
            message: PropTypes.string,
        }),
        PropTypes.bool,
    ]),
};

InputFiles.defaultProps = {
    label: null,
    texteAide:
        'La taille maximale autorisée de votre document est de 5Mo. Les formats acceptés sont .png, .pdf, .jpeg, .jpg',
    disabled: false,
    multiple: false,
    error: null,
    warning: null,
    valid: null,
    required: false,
    accept: '.jpg,.png,.jpeg,.pdf',
    tailleMoMaxUnFichier: 5,
    tailleMoMaxTotalFichiers: 18,
    nombreMaxFichiers: 3,
    licenceFichier: null,
};

export default InputFiles;
